import { useEffect, useRef, useState } from "react";

const HOME_UPDATES_STORAGE_KEY = "emalpha_home_updates_cache_v1";
const MAX_HOME_UPDATES = 12;
const ROTATION_INTERVAL_MS = 3000;
const INITIAL_LOADING_GRACE_MS = 5000;

export const getHomeUpdateKey = (update = {}, fallbackIndex = 0) => {
  const contentParts = [
    update.title,
    update.description,
    update.timestamp,
    update.start_date,
    update.type,
    update.impact_score,
    update.question,
  ]
    .map((value) => (value == null ? "" : String(value).trim()))
    .filter(Boolean);

  if (contentParts.length) {
    return contentParts.join("::");
  }

  const fallbackKey = update.id == null ? "" : String(update.id).trim();
  return fallbackKey || `home-update-${fallbackIndex}`;
};

const normalizeHomeUpdates = (updates = []) => {
  const seen = new Set();

  return (Array.isArray(updates) ? updates : [])
    .filter(Boolean)
    .map((update) => ({
      ...update,
      title: update.title ?? "",
      description: update.description ?? "",
      type: update.type ?? "insight",
      timestamp:
        update.timestamp ?? update.timeStamp ?? update.published_at ?? null,
      start_date: update.start_date ?? null,
      impact_score:
        update.impact_score == null || update.impact_score === ""
          ? null
          : Number.isFinite(Number(update.impact_score))
            ? Math.max(-1, Math.min(1, Number(update.impact_score)))
            : null,
      question:
        typeof update.question === "string" && update.question.trim()
          ? update.question.trim()
          : null,
    }))
    .filter((update, index) => {
      const key = getHomeUpdateKey(update, index);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_HOME_UPDATES);
};

const readCachedHomeUpdates = () => {
  try {
    const raw = localStorage.getItem(HOME_UPDATES_STORAGE_KEY);
    if (!raw) return [];

    return normalizeHomeUpdates(JSON.parse(raw));
  } catch (error) {
    console.error("Unable to restore cached home updates:", error);
    return [];
  }
};

export const useHomeUpdatesFeed = (
  incomingUpdates,
  incomingLoading,
  { hasPendingIncoming = false, lastReceivedAt = 0, enableRotation = true } = {},
) => {
  const [displayUpdates, setDisplayUpdates] = useState(() =>
    readCachedHomeUpdates(),
  );
  const [isRotatingFallback, setIsRotatingFallback] = useState(false);
  const [showLoadingFallback, setShowLoadingFallback] = useState(
    displayUpdates.length === 0,
  );
  const lastReceivedAtRef = useRef(lastReceivedAt);

  useEffect(() => {
    const normalizedIncoming = normalizeHomeUpdates(incomingUpdates);
    if (!normalizedIncoming.length) return;

    setDisplayUpdates((current) => {
      const currentKeys = new Set(
        current.map((update, index) => getHomeUpdateKey(update, index)),
      );
      const newUpdates = normalizedIncoming.filter(
        (update, index) => !currentKeys.has(getHomeUpdateKey(update, index)),
      );

      if (!newUpdates.length && current.length) {
        return current;
      }

      return normalizeHomeUpdates([...newUpdates, ...current]);
    });
  }, [incomingUpdates]);

  useEffect(() => {
    if (!displayUpdates.length) return;

    try {
      localStorage.setItem(
        HOME_UPDATES_STORAGE_KEY,
        JSON.stringify(displayUpdates.slice(0, MAX_HOME_UPDATES)),
      );
    } catch (error) {
      console.error("Unable to cache home updates:", error);
    }
  }, [displayUpdates]);

  useEffect(() => {
    if (!incomingLoading || displayUpdates.length > 0) {
      setShowLoadingFallback(false);
      return undefined;
    }

    setShowLoadingFallback(true);
    const timeoutId = setTimeout(() => {
      setShowLoadingFallback(false);
    }, INITIAL_LOADING_GRACE_MS);

    return () => clearTimeout(timeoutId);
  }, [incomingLoading, displayUpdates.length]);

  useEffect(() => {
    if (!lastReceivedAt || lastReceivedAt === lastReceivedAtRef.current) {
      return;
    }

    lastReceivedAtRef.current = lastReceivedAt;
    setIsRotatingFallback(false);
  }, [lastReceivedAt]);

  useEffect(() => {
    if (!enableRotation) return undefined;
    if (displayUpdates.length < 2) return undefined;

    const intervalId = setInterval(() => {
      if (hasPendingIncoming) {
        setIsRotatingFallback(false);
        return;
      }

      setIsRotatingFallback(true);
      setDisplayUpdates((current) => {
        if (current.length < 2) return current;
        return [...current.slice(1), current[0]];
      });
    }, ROTATION_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [displayUpdates.length, enableRotation, hasPendingIncoming]);

  return {
    displayUpdates,
    isRotatingFallback,
    showLoading:
      incomingLoading && displayUpdates.length === 0 && showLoadingFallback,
    showEmptyState:
      (!incomingLoading || !showLoadingFallback) && displayUpdates.length === 0,
  };
};
